import { Input } from './ui';

/**
 * A5b.8 — the live tile's optional colour threshold. Edits a JSON object in a
 * single-line config value: `{"gte":100,"colour":"accent"}` (or `lte`), the shape
 * `compilePreset` maps into `live_tile.props.thresholds`.
 *
 * Empty string means "no threshold", which is valid — the dashboard preset's
 * `threshold` is optional, so this control never writes a half-formed object.
 *
 * `colour` is a semantic token name (accent, ok, warn, danger, ink, …), never a
 * colour literal: `docs/style.md` bans literals and the style gate enforces it.
 */

const TOKENS = ['accent', 'ok', 'warn', 'danger', 'ink', 'ink-muted'];

/** Parse the config value; a missing/blank `colour` falls back to `accent`. */
export function parseThreshold(value) {
  if (!value.trim()) return null;
  try {
    const parsed = JSON.parse(value);
    if (!parsed || typeof parsed !== 'object' || Array.isArray(parsed)) return null;
    const record = parsed;
    return {
      ...(typeof record.gte === 'number' ? { gte: record.gte } : {}),
      ...(typeof record.lte === 'number' ? { lte: record.lte } : {}),
      colour: typeof record.colour === 'string' && record.colour ? record.colour : 'accent'
    };
  } catch {
    return null;
  }
}

function serialise(threshold) {
  return threshold === null ? '' : JSON.stringify(threshold);
}

export function ThresholdEditor({ id, value, onChange }) {
  const threshold = parseThreshold(value);

  function enable() {
    onChange(serialise({ gte: 0, colour: 'accent' }));
  }

  function patch(changes) {
    if (!threshold) return;
    onChange(serialise({ ...threshold, ...changes }));
  }

  return (
    <div className="threshold-editor" id={id}>
      {threshold === null ? (
        <button className="button button-quiet" type="button" onClick={enable}>
          Add threshold
        </button>
      ) : (
        <>
          <label className="index-label" htmlFor={`${id}-gte`}>
            ABOVE (GTE)
          </label>
          <Input
            id={`${id}-gte`}
            type="number"
            inputMode="numeric"
            value={threshold.gte ?? ''}
            onChange={(event) => patch({ gte: event.target.value === '' ? undefined : Number(event.target.value) })}
          />
          <label className="index-label" htmlFor={`${id}-lte`}>
            BELOW (LTE, OPTIONAL)
          </label>
          <Input
            id={`${id}-lte`}
            type="number"
            inputMode="numeric"
            value={threshold.lte ?? ''}
            onChange={(event) => patch({ lte: event.target.value === '' ? undefined : Number(event.target.value) })}
          />
          <label className="index-label" htmlFor={`${id}-colour`}>
            COLOUR TOKEN
          </label>
          <select
            id={`${id}-colour`}
            className="control select"
            value={threshold.colour}
            onChange={(event) => patch({ colour: event.target.value })}
          >
            {TOKENS.map((token) => (
              <option key={token} value={token}>
                {token}
              </option>
            ))}
          </select>
          <p className="detail-note">Token names only — a colour literal is rejected by the style gate.</p>
          <button
            className="button button-quiet"
            type="button"
            onClick={() => onChange('')}
          >
            Remove threshold
          </button>
        </>
      )}
    </div>
  );
}
